import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import "./large-tooltip.css"

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface DescriptionTooltipProps {
  isOpen: boolean;
  onClose: () => void;
  description: React.ReactNode | string;
  position?: { top: number; left: number };
  title?: string;
  type?: any;
  placement?: 'top' | 'bottom';
}

const LargeTooltip = ({
  isOpen,
  onClose,
  description,
  position,
  title = "Description",
  type = "default",
  placement = 'bottom',
}: DescriptionTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedStyle, setAdjustedStyle] = useState<{ top: number; left: number; arrowTop: number } | null>(null);

  useLayoutEffect(() => {
    if (isOpen && position && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const rect = tooltip.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      let computedTop = placement === 'top' ? position.top : position.top - 35;
      let computedLeft = position.left + 30;
      let arrowTop = 24;

      if (computedLeft + rect.width > screenWidth) {
        computedLeft = screenWidth - rect.width - 10;
      }
      if (computedLeft < 10) {
        computedLeft = 10;
      }

      // Check vertical overflow relative to viewport
      const viewportY = computedTop - window.scrollY;
      const tooltipBottom = viewportY + rect.height;

      if (tooltipBottom > screenHeight - 10) {
        const overflowAmount = tooltipBottom - (screenHeight - 10);
        computedTop -= overflowAmount;
        // Move the arrow down by the same amount so it still points to the target
        arrowTop += overflowAmount;
      }

      // Check if top overflowed
      const newViewportY = computedTop - window.scrollY;
      if (newViewportY < 10) {
        const topOverflow = 10 - newViewportY;
        computedTop += topOverflow;
        arrowTop -= topOverflow;
      }

      setAdjustedStyle({ top: computedTop, left: computedLeft, arrowTop });
    } else {
      setAdjustedStyle(null);
    }
  }, [isOpen, position, description, placement]);

  // Close on outside click. Since this fires on mousedown -- before the click event
  // that would open a *different* trigger's tooltip -- clicking another "View more"
  // button closes this one first, giving "only one open at a time" for free without
  // any cross-instance coordination between the many independent call sites.
  // onClose is read via a ref (most callers pass a fresh function identity every
  // render) so the listener attaches once per open/close instead of tearing down
  // and re-adding on every render while a tooltip is open.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onCloseRef.current();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!isOpen) return null;

  const isHtmlString = typeof description === "string";

  // Initial render style to allow measurement
  const renderStyle = adjustedStyle 
    ? { top: adjustedStyle.top, left: adjustedStyle.left } 
    : (position ? { top: placement === 'top' ? position.top : position.top - 35, left: position.left + 30, opacity: 0 } : {});

  const content = (
    <div
      ref={tooltipRef}
      className={`large-tooltip large-tooltip--right arrow-at-start`}
      style={renderStyle}
    >
      <div 
        className="tooltip-arrow" 
        style={adjustedStyle ? { top: adjustedStyle.arrowTop } : {}}
      ></div>

      <div className="tooltip-header">
        <h3 className="tooltip-title">{title}</h3>
        <button className="tooltip-close-button" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <div className="tooltip-content">
        <div className="tooltip-description">
          {isHtmlString ? (
            <div dangerouslySetInnerHTML={{ __html: description as string }} />
          ) : (
            description
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
};

export default LargeTooltip;
